import type { HeardAboutUs } from "@repo/vihangam-yog-shared";
import { useCallback, useState } from "react";

import {
  PARTY_GENDERS,
  emptyMember,
  type PartyGender,
  type PartyMember,
  type PartyNeed,
} from "./PartyEditor";
import { type LastHawan, isLastHawan } from "./lastHawan";

export const PAY_VIA = ["paynow", "card"] as const;
export type PayVia = (typeof PAY_VIA)[number];

function isPayVia(value: string | undefined): value is PayVia {
  return PAY_VIA.some((v) => v === value);
}

export interface BundleDraft {
  name: string;
  mobile: string;
  email: string;
  party: PartyMember[];
  /** The person filling the form is the first row of the party, named from
   *  the contact name, so nobody types their own name twice. Off when they
   *  register others and stay home. */
  selfIncluded: boolean;
  stage: boolean;
  hawan: boolean;
  tierId: string;
  food: "vegetarian" | "jain" | "";
  heard: HeardAboutUs | "";
  /** Who told them, asked only when `heard` is a person or WhatsApp. */
  heardDetail: string;
  /** When they last did a Hawan; required once Sunday is ticked. */
  lastHawan: LastHawan | "";
  whatsappJoined: boolean;
  /** The group or organisation the party comes with, if any. */
  groupName: string;
  /** A question, or anything the organisers should know. */
  note: string;
  /** A discount code for Sunday, checked by the server at submit. */
  discountCode: string;
  /** How Sunday is paid; PayNow is offered first and is the default. */
  payVia: PayVia;
  acknowledged: boolean;
}

const KEY = "vysg-register-draft";

/** A draft older than this is dropped: by then the visit has moved on. */
const DRAFT_TTL_MS = 14 * 24 * 60 * 60 * 1000;

const EMPTY_DRAFT: BundleDraft = {
  name: "",
  mobile: "",
  email: "",
  party: [emptyMember("adult")],
  selfIncluded: true,
  stage: false,
  hawan: false,
  tierId: "",
  food: "",
  heard: "",
  heardDetail: "",
  lastHawan: "",
  whatsappJoined: false,
  groupName: "",
  note: "",
  discountCode: "",
  payVia: "paynow",
  acknowledged: false,
};

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the sessionStorage JSON
function isNeed(value: unknown): value is PartyNeed {
  return value === "wheelchair" || value === "chair";
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the sessionStorage JSON
function isGender(value: unknown): value is PartyGender {
  return PARTY_GENDERS.some((g) => g === value);
}

/** A persisted party row from any version of the draft: name and kind are
 *  the constants; age, needs and gender arrived later and are read leniently. */
interface StoredMember {
  name: string;
  kind: "adult" | "child";
  age?: unknown;
  needs?: unknown;
  gender?: unknown;
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the sessionStorage JSON
function isStoredMember(value: unknown): value is StoredMember {
  return (
    typeof value === "object" &&
    value !== null &&
    "name" in value &&
    typeof value.name === "string" &&
    "kind" in value &&
    (value.kind === "adult" || value.kind === "child")
  );
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the sessionStorage JSON
function isAgeText(value: unknown): value is string {
  return typeof value === "string";
}

/**
 * Rows written before the age, needs and gender fields existed get
 * defaults, so a visitor who comes back mid-registration finds their party
 * intact rather than an editor that throws on `needs.includes`.
 */
function toMember(m: StoredMember): PartyMember {
  return {
    name: m.name,
    kind: m.kind,
    age: isAgeText(m.age) ? m.age : "",
    needs: Array.isArray(m.needs) ? m.needs.filter(isNeed) : [],
    gender: isGender(m.gender) ? m.gender : null,
  };
}

/** What the stored JSON may hold: any version of the draft, with the
 *  save stamp on the newer ones. */
type StoredDraft = Partial<BundleDraft> & {
  party: unknown[];
  savedAt?: unknown;
};

/** A draft loaded from the device, and whether it had anything in it. */
interface LoadedDraft {
  draft: BundleDraft;
  restored: boolean;
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate: this IS the parse boundary for the stored JSON
function isDraft(value: unknown): value is StoredDraft {
  return (
    typeof value === "object" &&
    value !== null &&
    "party" in value &&
    Array.isArray(value.party) &&
    "name" in value &&
    typeof value.name === "string"
  );
}

/** Drafts saved before the stamp existed have none and are kept. */
function hasSavedAt(
  value: StoredDraft,
): value is StoredDraft & { savedAt: string } {
  return typeof value.savedAt === "string";
}

/** A draft worth telling the visitor about: something typed or ticked. */
function hasContent(d: BundleDraft): boolean {
  return (
    d.name.trim() !== "" ||
    d.email.trim() !== "" ||
    d.mobile.trim() !== "" ||
    d.stage ||
    d.hawan ||
    d.party.some((p) => p.name.trim() !== "" || p.age.trim() !== "")
  );
}

function load(): LoadedDraft {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return { draft: EMPTY_DRAFT, restored: false };
    }
    const parsed: unknown = JSON.parse(raw);
    if (!isDraft(parsed)) {
      return { draft: EMPTY_DRAFT, restored: false };
    }
    if (hasSavedAt(parsed)) {
      const savedAt = Date.parse(parsed.savedAt);
      if (!Number.isNaN(savedAt) && Date.now() - savedAt > DRAFT_TTL_MS) {
        localStorage.removeItem(KEY);
        return { draft: EMPTY_DRAFT, restored: false };
      }
    }
    const { savedAt: _savedAt, ...stored } = parsed;
    const party = stored.party.filter(isStoredMember).map(toMember);
    const draft: BundleDraft = {
      ...EMPTY_DRAFT,
      ...stored,
      party: party.length > 0 ? party : EMPTY_DRAFT.party,
      // Drafts saved before the question existed have no answer yet.
      lastHawan: isLastHawan(parsed.lastHawan) ? parsed.lastHawan : "",
      payVia: isPayVia(parsed.payVia) ? parsed.payVia : "paynow",
      // Consent is given per submission, never carried over.
      acknowledged: false,
    };
    return { draft, restored: hasContent(draft) };
  } catch {
    return { draft: EMPTY_DRAFT, restored: false };
  }
}

function save(draft: BundleDraft): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ ...draft, savedAt: new Date().toISOString() }),
    );
  } catch {
    // storage unavailable (private mode); the form still works for this visit
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

/**
 * The form's state, kept on the device so a closed tab, a Stripe round
 * trip or a phone put down for the day does not start the form over. The
 * thanks page clears it once a registration is through.
 */
export function useBundleDraft() {
  const [loaded] = useState(load);
  const [draft, setDraft] = useState<BundleDraft>(loaded.draft);
  const set = useCallback((patch: Partial<BundleDraft>) => {
    setDraft((prev) => {
      const next = { ...prev, ...patch };
      save(next);
      return next;
    });
  }, []);
  const reset = useCallback(() => {
    clearDraft();
    setDraft(EMPTY_DRAFT);
  }, []);
  return { draft, set, reset, restored: loaded.restored };
}
